import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, XCircle, ArrowRight } from 'lucide-react';
import { useEditOrder } from '../../Common/EditOrder';
import editBlockReasonFor from '../../Common/EditOrder/editBlockReasonCopy';
import Header from '../Common/Header';
import { SimpleProduct, PackageProduct } from '@ajat/design-system/ShoppingCart';
import Button from '@ajat/design-system/Button';
import EmptyState from '@ajat/design-system/EmptyState';
import InlineNotice from '@ajat/design-system/InlineNotice';
import Hyperlink from '@ajat/design-system/Hyperlink';
import Popup from '@ajat/design-system/Popup';
import '../../Common/EditOrder/EditOrder.css';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-SE — Edit Order Page
 * Uses Checkout Header (logo only) + ShoppingCart-like product list
 * with Order Details block (billing + payment info) on top.
 * No Summary sidebar.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ── Custom icons from Figma (shared across brands) ── */
const SvgBillingInfo = require('../../../../assets/common/icon-billing-info.svg').ReactComponent;
const SvgOrderStatus = require('../../../../assets/common/icon-order-status.svg').ReactComponent;

const BillingIcon = () => <SvgBillingInfo className="edit-order__svg-icon" />;
const PaymentIcon = () => <SvgOrderStatus className="edit-order__svg-icon" />;

/* ── Swedish notifications ── */
const NOTIFICATIONS = {
  updated: {
    title: 'Produkt uppdaterad',
    message: (orderNumber) => `Beställning #${orderNumber} har uppdaterats.`,
  },
  quantity: {
    title: 'Beställning uppdateras',
    message: (total) => `Totalt: ${total}`,
  },
  removed: {
    title: 'Produkt borttagen',
    message: (name, total) => `${name} har tagits bort. Totalt: ${total}`,
  },
};

const OPTION_LABELS = { color: 'Färg', size: 'Storlek', namn: 'Namntryck' };

const EditOrder = () => {
  const navigate = useNavigate();

  const {
    order,
    loading,
    error,
    allowsPriceChanges,
    notEditableReason,
    notEditableReasonCode,
    adminMode,
    currentOrderNumber,
    cancelled,
    showCancelPopup,
    setShowCancelPopup,
    removeTarget,
    setRemoveTarget,
    priceSuffix,
    totalExMoms,
    momsAmount,
    totalInclMoms,
    formatPrice,
    handleQuantityChange,
    requestRemove,
    confirmRemove,
    handleCancelConfirm,
    buildOptions,
  } = useEditOrder({
    brand: 'CLS',
    apiBrandSlug: 'clsse',
    priceSuffix: ':-',
    locale: 'sv-SE',
    momsRate: 0.25,
    optionLabels: OPTION_LABELS,
    addonKeys: [{ dataKey: 'navnetryk', labelKey: 'namn' }],
    notifications: NOTIFICATIONS,
    priceLockedMessage: 'Den här beställningen är redan betald, så ändringar som påverkar priset är inte möjliga. Vill du göra större ändringar, avbryt beställningen (du blir återbetald) och gör en ny.',
  });

  const priceLocked = !allowsPriceChanges;
  // Reconstruct the order's full scope (Season → School → Shop → Class) so every
  // edit reopens the variation exactly as it was bought.
  const className = order.scope?.className ?? order.school?.class ?? null;
  const schoolSlug = order.schoolSlug ?? order.scope?.school?.slug ?? null;
  const shopSlug = order.shopSlug ?? order.scope?.shop?.slug ?? null;
  const editState = (section, p) => ({
    editOrder: {
      orderNumber: currentOrderNumber, itemId: p.id, section,
      options: p.options, quantity: p.quantity, priceLocked,
      targetPrice: p.unitPrice, schoolSlug: schoolSlug ?? p.schoolSlug, shopSlug, className,
      scope: order.scope,
    },
  });

  // Reopen the variation (with shop/class overrides) when we have that context,
  // so the price matches the order line exactly; fall back to the product PDP.
  // Which route reopens a line depends on where the line was BOUGHT, not on
  // which school the order was finally attributed to. A school-shop line
  // reopens inside its shop so class overrides still apply. A collection-link
  // or open-shop line has no shop behind it, and the school-shop variation
  // route resolves through the school's OPEN SHOPS — so pointing those at it
  // produced "Product not found". They reopen the bare PDP instead, carrying
  // the variation (and its collection) so the tier price still resolves.
  const lineCollection = (p) => p.options?.collectionSlug || null;
  const lineFromSchoolShop = (p) => !lineCollection(p) && (!!p.options?.schoolSlug || !!shopSlug);
  const variationSchool = (p) => (lineFromSchoolShop(p) ? (p.schoolSlug ?? schoolSlug) : null);
  const editPath = (p) => {
    if (p.variationId && variationSchool(p)) {
      return `/clothing/cls-se/school-shop/${variationSchool(p)}/product/${p.variationId}`;
    }
    if (p.variationId && p.slug) {
      const coll = lineCollection(p);
      return `/clothing/cls-se/product-details/${p.slug}?variation=${encodeURIComponent(p.variationId)}`
        + (coll ? `&collection=${encodeURIComponent(coll)}` : '');
    }
    return `/clothing/cls-se/product-details/${p.slug}`;
  };

  const canEdit = (p) => !notEditableReason && (!!(p.variationId && variationSchool(p)) || !!p.slug);

  return (
    <div className="edit-order">
      <Header brand="CLS" mode="checkout" checkoutLabel="Säker Redigering" backLabel="Tillbaka" showTopBar={false} hideOnScroll={false} onBackClick={() => navigate('/clothing/cls-se/shopping-cart')} />

      <main className="edit-order__main">
        {loading ? (
          <EmptyState message="Laddar din beställning…" />
        ) : error === 'expired' ? (
          <EmptyState icon={XCircle} title="Länken har gått ut" message="Din redigeringslänk har gått ut. Logga in igen för att ändra din beställning." />
        ) : error ? (
          <EmptyState icon={XCircle} title="Något gick fel" message={error} />
        ) : cancelled ? (
          <EmptyState
            icon={XCircle}
            title="Beställningen har avbrutits"
            message="Din beställning har avbrutits. Du kan börja om från skolshoppen."
            actions={[{ label: 'Börja om', variant: 'main', size: 'secondary', iconRight: <ArrowRight size={20} />, onClick: () => navigate('/clothing/cls-se/school-shop') }]}
          />
        ) : (
        <div className="edit-order__container">
          {adminMode && (
            <InlineNotice variant="warning" bordered textVariant="body">
              Admin edit mode — you are editing this order on behalf of the customer. Paid card orders stay price-neutral.
            </InlineNotice>
          )}
          {notEditableReason && (
            <InlineNotice variant="warning" bordered textVariant="body">{editBlockReasonFor('clsse', notEditableReasonCode, notEditableReason)}</InlineNotice>
          )}
          {!notEditableReason && priceLocked && (
            <InlineNotice variant="warning" bordered textVariant="body">
              Din beställning är betald. Du kan ändra färg, storlek och namntryck, men inte antal eller ta bort varor.
              Vill du göra större ändringar kan du avbryta beställningen (du blir återbetald) och beställa på nytt.
            </InlineNotice>
          )}
          {/* ── Order Details card ── */}
          <section className="edit-order__details-card">
            <div className="edit-order__details-inner">
              <div className="edit-order__details-title-block">
                <p className="edit-order__details-order-number typo-body-sm">#{currentOrderNumber}</p>
                <h2 className="edit-order__details-title typo-h3">Orderdetaljer</h2>
              </div>

              <div className="edit-order__details-wrapper">
                {/* Billing Address */}
                <div className="edit-order__billing">
                  <div className="edit-order__billing-row">
                    <div className="edit-order__billing-icon">
                      <BillingIcon />
                    </div>
                    <div className="edit-order__billing-copy">
                      <span className="edit-order__billing-label typo-body">Faktureringsadress</span>
                      <span className="edit-order__billing-name typo-h4">{order.billing.name}</span>
                      <div className="edit-order__billing-lines typo-body-sm">
                        <p>{order.billing.address}</p>
                        <p>{order.billing.contact}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="edit-order__payment">
                  <div className="edit-order__payment-row">
                    <div className="edit-order__payment-icon">
                      <PaymentIcon />
                    </div>
                    <div className="edit-order__payment-copy">
                      <span className="edit-order__payment-label typo-body">Betalningsuppgifter</span>
                      <div className="edit-order__payment-lines">
                        <div className="edit-order__payment-line">
                          <span className="typo-h6">TOTALT UTAN MOMS</span>
                          <span className="typo-h6">{formatPrice(totalExMoms)}</span>
                        </div>
                        <div className="edit-order__payment-line">
                          <span className="typo-h6">MOMS</span>
                          <span className="typo-h6">{formatPrice(momsAmount)}</span>
                        </div>
                        <div className="edit-order__payment-line edit-order__payment-line--total">
                          <span className="typo-h6">TOTAL</span>
                          <span className="typo-h6">{formatPrice(totalInclMoms)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Products section ── */}
          {order.products.length > 0 && (
            <section className="edit-order__section">
              <div className="edit-order__section-inner">
                <h2 className="edit-order__section-title typo-h3">Produkter</h2>
                <div className="edit-order__products">
                  {order.products.map((p) => (
                    <SimpleProduct
                      key={p.id}
                      image={p.options?.image || p.image}
                      imageAlt={p.name}
                      title={p.name}
                      unitPrice={p.unitPrice}
                      priceSuffix={priceSuffix}
                      options={buildOptions(p.options)}
                      quantity={p.quantity}
                      lockQuantity={priceLocked || !!notEditableReason}
                      onQuantityChange={(q) => handleQuantityChange('products', p.id, q)}
                      onEdit={canEdit(p) ? () => navigate(editPath(p), { state: editState('products', p) }) : undefined}
                      onRemove={(priceLocked || notEditableReason) ? undefined : () => requestRemove('products', p.id, p.name)}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ── Packages section ── */}
          {order.packages.length > 0 && (
            <section className="edit-order__section">
              <div className="edit-order__section-inner">
                <h2 className="edit-order__section-title typo-h3">Paket</h2>
                <div className="edit-order__packages">
                  {order.packages.map((pkg) => (
                    <PackageProduct
                      key={pkg.id}
                      image={pkg.options?.image || pkg.image}
                      imageAlt={pkg.name}
                      title={pkg.name}
                      unitPrice={pkg.unitPrice}
                      unitOriginalPrice={pkg.unitOriginalPrice}
                      priceState={pkg.unitOriginalPrice ? 'discounted' : 'default'}
                      priceSuffix={priceSuffix}
                      savingsText={pkg.savingsText}
                      quantity={pkg.quantity}
                      lockQuantity={priceLocked || !!notEditableReason}
                      onQuantityChange={(q) => handleQuantityChange('packages', pkg.id, q)}
                      onEdit={notEditableReason ? undefined : () => navigate(`/clothing/cls-se/product-package/${pkg.slug}`, {
                        state: { editOrder: { orderNumber: currentOrderNumber, itemId: pkg.id, section: 'packages', includedProducts: pkg.includedProducts, quantity: pkg.quantity, priceLocked, targetPrice: pkg.unitPrice } },
                      })}
                      onRemove={(priceLocked || notEditableReason) ? undefined : () => requestRemove('packages', pkg.id, pkg.name)}
                      includedTitle="INKLUDEREDE PRODUKTER"
                      includedProducts={(pkg.options?.includedProducts || pkg.includedProducts || []).map((ip) => ({
                        title: ip.productName || ip.title || ip.groupName,
                        subtitle: ip.subtitle || [ip.color, ip.size, ip.addons?.navnetryk].filter(Boolean).join(', '),
                        image: ip.image,
                      }))}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ── Student Club section ── */}
          {order.studentClub.length > 0 && (
            <section className="edit-order__section">
              <div className="edit-order__section-inner">
                <h2 className="edit-order__section-title typo-h3">Student club</h2>
                <div className="edit-order__products">
                  {order.studentClub.map((p) => (
                    <SimpleProduct
                      key={p.id}
                      image={p.options?.image || p.image}
                      imageAlt={p.name}
                      title={p.name}
                      unitPrice={p.unitPrice}
                      priceSuffix={priceSuffix}
                      options={buildOptions(p.options)}
                      quantity={p.quantity}
                      lockQuantity={priceLocked || !!notEditableReason}
                      onQuantityChange={(q) => handleQuantityChange('studentClub', p.id, q)}
                      onEdit={canEdit(p) ? () => navigate(editPath(p), { state: editState('studentClub', p) }) : undefined}
                      onRemove={(priceLocked || notEditableReason) ? undefined : () => requestRemove('studentClub', p.id, p.name)}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ── Cancel order CTA ── */}
          {!notEditableReason && (
            <div className="edit-order__cancel">
              <Button
                size="secondary"
                variant="danger"
                iconLeft={<X size={20} />}
                onClick={() => setShowCancelPopup(true)}
              >
                AVBESTÄLL
              </Button>
            </div>
          )}
        </div>
        )}
      </main>

      <Popup
        isOpen={showCancelPopup}
        onClose={() => setShowCancelPopup(false)}
        onConfirm={handleCancelConfirm}
        title="Avbryt beställning"
        message="Är du säker på att du vill avbryta din beställning? Denna åtgärd kan inte ångras."
        showInfo={false}
        cancelLabel="Tillbaka"
        confirmLabel="Avbryt beställning"
        confirmVariant="danger"
      />

      <Popup
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title="Ta bort produkt"
        message={`Är du säker på att du vill ta bort ${removeTarget?.name || 'denna produkt'} från din beställning?`}
        showInfo={false}
        cancelLabel="Tillbaka"
        confirmLabel="Ta bort"
        confirmVariant="danger"
      />

      <footer className="edit-order__footer">
        <div className="edit-order__footer-inner">
          <span className="edit-order__footer-copy typo-body">© {new Date().getFullYear()} C.L. Seifert A/S, Denmark, All Rights Reserved.</span>
          <Hyperlink variant="regular" href="#">Kontakt os</Hyperlink>
          <Hyperlink variant="regular" href="#">Handelsbetingelser</Hyperlink>
        </div>
      </footer>
    </div>
  );
};

export default EditOrder;
